import React, { useState, useCallback } from 'react';
import Grid from '@mui/material/GridLegacy';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Chip, TextField, InputAdornment, IconButton, Tooltip } from '@mui/material';
import { Edit, Map as MapIcon, ContentCopy, Check } from '@mui/icons-material';
import { DIALOG_CONTENT_SX } from '../../../theme';
import { formatCategoryName } from '../constants';
import { getImageDisplayUrl } from '../../../config/api';

function mongoIdString(id) {
  if (id == null) return '';
  if (typeof id === 'string') return id;
  if (typeof id === 'object' && id.$oid) return id.$oid;
  return String(id);
}

export default function PlaceDetailsDialog({
  open,
  onClose,
  place,
  onEditPlace,
}) {
  const [copied, setCopied] = useState(false);
  const placeIdStr = place ? mongoIdString(place._id) : '';

  const handleCopyPlaceId = useCallback(async () => {
    if (!placeIdStr) return;
    try {
      await navigator.clipboard.writeText(placeIdStr);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      try {
        const ta = document.createElement('textarea');
        ta.value = placeIdStr;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      } catch {
        /* ignore */
      }
    }
  }, [placeIdStr]);

  if (!place) return null;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)' } }}
    >
      <DialogTitle sx={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        fontWeight: 700,
        py: 3,
        px: 4
      }}>
        Place Details
      </DialogTitle>
      <DialogContent sx={DIALOG_CONTENT_SX}>
        <Box sx={{ pt: 1 }}>
          <Box sx={{ p: 2, borderRadius: 2, background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)', border: '1px solid rgba(102, 126, 234, 0.1)', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mb: 0.5 }}>Basic Information</Typography>
            <Typography variant="body2" color="text.secondary">Essential details about the place</Typography>
          </Box>

          {placeIdStr && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75, fontWeight: 600 }}>
                Place ID (for Redemptions filters &amp; Load place stats)
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={placeIdStr}
                InputProps={{
                  readOnly: true,
                  sx: { fontFamily: 'ui-monospace, monospace', fontSize: '0.85rem' },
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={copied ? 'Copied' : 'Copy to clipboard'}>
                        <IconButton
                          edge="end"
                          onClick={handleCopyPlaceId}
                          color={copied ? 'success' : 'primary'}
                          aria-label="Copy place ID"
                        >
                          {copied ? <Check fontSize="small" /> : <ContentCopy fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                size="small"
                variant="outlined"
                startIcon={copied ? <Check /> : <ContentCopy />}
                onClick={handleCopyPlaceId}
                sx={{ mt: 1, textTransform: 'none' }}
                color={copied ? 'success' : 'primary'}
              >
                {copied ? 'Copied to clipboard' : 'Copy place ID'}
              </Button>
            </Box>
          )}

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <Box sx={{ p: 2.5, borderRadius: 2, bgcolor: 'rgba(0, 0, 0, 0.02)', border: '1px solid rgba(0, 0, 0, 0.05)' }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>{place.name}</Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  {place.description && place.description !== place.name ? place.description : 'No description provided'}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {place.priceRange && (
                    <Chip label={place.priceRange} size="small" sx={{
                      bgcolor: place.priceRange === 'free' ? 'rgba(46, 125, 50, 0.1)' : place.priceRange === 'high' ? 'rgba(211, 47, 47, 0.1)' : 'rgba(158, 158, 158, 0.1)',
                      color: place.priceRange === 'free' ? 'success.main' : place.priceRange === 'high' ? 'error.main' : 'text.secondary',
                      fontWeight: 500
                    }} />
                  )}
                  <Chip label={place.status} size="small" sx={{
                    bgcolor: place.status === 'accepted' ? 'rgba(46, 125, 50, 0.1)' : place.status === 'pending_approval' ? 'rgba(237, 108, 2, 0.1)' : 'rgba(211, 47, 47, 0.1)',
                    color: place.status === 'accepted' ? 'success.main' : place.status === 'pending_approval' ? 'warning.main' : 'error.main',
                    fontWeight: 500,
                    textTransform: 'capitalize'
                  }} />
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(0, 0, 0, 0.02)', border: '1px solid rgba(0, 0, 0, 0.05)' }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Address</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{place.location?.address || place.address || 'N/A'}</Typography>
              </Box>
            </Grid>
            {(place.location?.googleMapsLink || place.googleMapsLink) && (
              <Grid item xs={12} sm={6}>
                <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(0, 0, 0, 0.02)', border: '1px solid rgba(0, 0, 0, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Google Maps</Typography>
                  <Button size="small" variant="outlined" startIcon={<MapIcon />} href={place.location?.googleMapsLink || place.googleMapsLink} target="_blank" rel="noopener noreferrer" sx={{ textTransform: 'none', fontWeight: 500, borderRadius: 2 }}>View on Maps</Button>
                </Box>
              </Grid>
            )}
          </Grid>

          <Box sx={{ p: 2, borderRadius: 2, background: 'linear-gradient(135deg, rgba(79, 172, 254, 0.05) 0%, rgba(0, 242, 254, 0.05) 100%)', border: '1px solid rgba(79, 172, 254, 0.1)', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'info.main', mb: 0.5 }}>Categories & Tags</Typography>
            <Typography variant="body2" color="text.secondary">Classification and characteristics</Typography>
          </Box>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(0, 0, 0, 0.02)', border: '1px solid rgba(0, 0, 0, 0.05)' }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Category</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {place.category ? (
                    <Chip label={formatCategoryName(place.category)} size="small" sx={{ bgcolor: 'rgba(102, 126, 234, 0.1)', color: 'primary.main', fontWeight: 500 }} />
                  ) : place.categories && place.categories.length > 0 ? (
                    <Chip label={formatCategoryName(place.categories[0])} size="small" sx={{ bgcolor: 'rgba(102, 126, 234, 0.1)', color: 'primary.main', fontWeight: 500 }} />
                  ) : (
                    <Typography variant="body2" color="text.secondary">No category</Typography>
                  )}
                </Box>
              </Box>
            </Grid>
            {place.primaryTag && (
              <Grid item xs={12}>
                <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(0, 0, 0, 0.02)', border: '1px solid rgba(0, 0, 0, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Primary Tag (For Recommendations)</Typography>
                  <Chip label={place.primaryTag} size="small" sx={{ bgcolor: 'rgba(46, 125, 50, 0.1)', color: 'success.main', fontWeight: 600, textTransform: 'capitalize' }} />
                </Box>
              </Grid>
            )}
            {((place.tags && place.tags.length > 0) || (place.vibeTags && place.vibeTags.length > 0)) && (
              <Grid item xs={12}>
                <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(0, 0, 0, 0.02)', border: '1px solid rgba(0, 0, 0, 0.05)' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Tags</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {(place.tags || place.vibeTags || []).map((tag, index) => (
                      <Chip key={index} label={tag} size="small" variant="outlined" sx={{ borderColor: 'rgba(102, 126, 234, 0.3)', color: 'primary.main' }} />
                    ))}
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>

          {place.comment && (
            <>
              <Box sx={{ p: 2, borderRadius: 2, background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.05) 0%, rgba(255, 152, 0, 0.05) 100%)', border: '1px solid rgba(255, 193, 7, 0.1)', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'warning.main', mb: 0.5 }}>Comment</Typography>
                <Typography variant="body2" color="text.secondary">Additional notes about this place</Typography>
              </Box>
              <Box sx={{ p: 2.5, borderRadius: 2, bgcolor: 'rgba(255, 193, 7, 0.05)', border: '1px solid rgba(255, 193, 7, 0.1)', mb: 3 }}>
                <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>{place.comment}</Typography>
              </Box>
            </>
          )}

          {place.openingHours && Object.values(place.openingHours).some((hours) => hours.open && hours.close) && (
            <>
              <Box sx={{ p: 2, borderRadius: 2, background: 'linear-gradient(135deg, rgba(67, 233, 123, 0.05) 0%, rgba(56, 249, 215, 0.05) 100%)', border: '1px solid rgba(67, 233, 123, 0.1)', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'success.main', mb: 0.5 }}>Opening Hours</Typography>
                <Typography variant="body2" color="text.secondary">Business hours and availability</Typography>
              </Box>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {Object.entries(place.openingHours).map(([day, hours]) => (
                  hours.open && hours.close && (
                    <Grid item xs={12} sm={6} key={day}>
                      <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(0, 0, 0, 0.02)', border: '1px solid rgba(0, 0, 0, 0.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ textTransform: 'capitalize', fontWeight: 500 }}>{day}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{hours.open} - {hours.close}</Typography>
                      </Box>
                    </Grid>
                  )
                ))}
              </Grid>
            </>
          )}

          {(place.redeemedOffers?.length > 0 || place.hasOffer) && (
            <>
              <Box sx={{ p: 2, borderRadius: 2, background: 'linear-gradient(135deg, rgba(46, 125, 50, 0.05) 0%, rgba(56, 249, 215, 0.05) 100%)', border: '1px solid rgba(46, 125, 50, 0.15)', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'success.main', mb: 0.5 }}>Redeemed offers</Typography>
                <Typography variant="body2" color="text.secondary">Total redemptions at this place (read-only)</Typography>
                <Typography variant="h5" sx={{ fontWeight: 600, mt: 1 }}>{(place.redeemedOffers || []).length}</Typography>
              </Box>
            </>
          )}

          {place.images && place.images.length > 0 && (
            <>
              <Box sx={{ p: 2, borderRadius: 2, background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)', border: '1px solid rgba(102, 126, 234, 0.1)', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mb: 0.5 }}>Images</Typography>
                <Typography variant="body2" color="text.secondary">Place photos and visuals</Typography>
              </Box>
              <Grid container spacing={2}>
                {place.images.map((image, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Box
                      component="img"
                      src={getImageDisplayUrl(image)}
                      alt={`${place.name} image ${index + 1}`}
                      sx={{
                        width: '100%',
                        height: 200,
                        objectFit: 'cover',
                        borderRadius: 2,
                        border: '1px solid rgba(0, 0, 0, 0.1)',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                        transition: 'all 0.3s ease',
                        '&:hover': { transform: 'scale(1.02)', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)' }
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, bgcolor: 'rgba(0, 0, 0, 0.02)' }}>
        <Button onClick={onClose} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500, px: 3 }}>Close</Button>
        {place && (
          <Button
            variant="contained"
            startIcon={<Edit />}
            onClick={() => { onEditPlace(place); onClose(); }}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500,
              px: 3,
              '&:hover': { background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)', boxShadow: '0 6px 20px rgba(102, 126, 234, 0.5)' }
            }}
          >
            Edit Place
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
